import React, { useState, useEffect } from 'react';
import {
  Search,
  MapPin,
  GraduationCap,
  Mail,
  School,
  User,
  ExternalLink,
  Loader,
  Filter,
  Sparkles
} from 'lucide-react';
import { searchProfessors, trackProfessorClick } from '../lib/professors';
import { useNavigate } from 'react-router-dom';
import { DEPARTMENT_CATEGORIES } from '../lib/constants';
import { UNIVERSITIES, UNIVERSITY_LIST } from '../lib/universities';  // Add UNIVERSITY_LIST here
import { getSubscriptionStatus, SUBSCRIPTION_TIERS } from '../lib/stripe';
import { SubscriptionGate } from './SubscriptionGate';
import { createProfessorInteraction } from "../lib/api";
import { motion, AnimatePresence } from 'framer-motion';
import { CustomDropdown } from './CustomDropdown';

export function ProfessorSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [professors, setProfessors] = useState<any[]>([]);
  const [subscriptionTier, setSubscriptionTier] = useState<string>(
    SUBSCRIPTION_TIERS.FREE
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (
        selectedCategory ||
        selectedDepartment ||
        selectedUniversity ||
        searchTerm
      ) {
        performSearch();
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, selectedCategory, selectedDepartment, selectedUniversity]);

  const performSearch = async () => {
    setLoading(true);
    setError(null);

    try {
      // For university searches, get the exact name without region
      const universityParam = selectedUniversity 
        ? selectedUniversity 
        : selectedUniversity;
      
      const { results, error: searchError } = await searchProfessors({
        searchTerm,
        department: selectedDepartment,
        university: universityParam,
        category: selectedCategory,
      });

      setProfessors(results);
      if (searchError) {
        setError(searchError);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      setProfessors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToEmailList = async (professor: any) => {
    trackProfessorClick(professor.id);

    const profInterId = await createProfessorInteraction({
      professor_id: professor.id,
      professor_name: professor.name,
      email: professor.email,
      notes: "Research interests: " + professor["research_interests"].join(", "),
      university: professor.university,
      website: professor.website,
      status: "new"
    });

    navigate('/emails', {
      state: {
        interactionId: profInterId
      },
    });
  };

  return (
    <motion.div 
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header - removed border */}
      <motion.div 
        className="bg-gray-800 rounded-xl overflow-hidden shadow-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="p-6">
          <h2 className="text-xl font-bold text-white">
            Find Professors
          </h2>
          <p className="mt-1 text-sm text-gray-400">
            Search for professors and researchers worldwide.
          </p>
        </div>
      </motion.div>

      {/* Search Filters */}
      <motion.div 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full rounded-lg bg-gray-900 text-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2.5" 
          />
        </div>

        <div>
          <CustomDropdown
            options={DEPARTMENT_CATEGORIES}
            value={selectedCategory}
            onChange={setSelectedCategory}
            placeholder="All Categories"
          />
        </div>

        {/* University dropdown temporarily hidden 
        <div>
          <CustomDropdown
            options={UNIVERSITY_LIST}
            value={selectedUniversity}
            onChange={setSelectedUniversity}
            placeholder="All Universities"
          />
        </div>
        */}
      </motion.div>

      {loading && (
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-indigo-500" />
            </div>
          </div>
        </div>
      )}

      {error && (
        <motion.div 
          className="bg-red-900/20 border border-red-800/30 rounded-lg p-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center space-x-2">
            <div className="flex-shrink-0">
              <ExternalLink className="h-5 w-5 text-red-400" />
            </div>
            <p className="text-sm text-red-300">{error}</p>
          </div>
        </motion.div>
      )}

      {/* Professor cards - no borders */}
      <AnimatePresence>
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          {professors.map((professor, index) => (
            <motion.div
              key={professor.id}
              className="bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300" // Removed border
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index, duration: 0.3 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-white">
                      {professor.name}
                    </h3>
                    <p className="text-sm text-gray-400">{professor.title}</p>
                  </div>
                  <div className="bg-gray-700/50 p-2 rounded-full">
                    <User className="h-6 w-6 text-indigo-400" />
                  </div>
                </div>

                <div className="space-y-4 mb-4">
                  <div className="flex items-center text-sm text-gray-400">
                    <School className="h-4 w-4 mr-2 text-gray-500" />
                    {professor.university}
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-300 mb-2">
                    Research Interests
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {professor["research_interests"]?.map((interest: any) => (
                      <span
                        key={interest}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-900/50 text-indigo-300 border border-indigo-800/50"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-2 mt-6">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleAddToEmailList(professor)}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-200 shadow-lg shadow-indigo-900/20"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Add to Email List
                  </motion.button>
                  {professor.website && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => window.open(professor.website, '_blank')}
                      className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>

      {!loading &&
        professors.length === 0 &&
        (searchTerm ||
          selectedCategory ||
          selectedDepartment ||
          selectedUniversity) && (
          <motion.div 
            className="text-center py-12 text-gray-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex flex-col items-center justify-center">
              <Search className="h-12 w-12 text-gray-600 mb-4" />
              <p className="text-lg font-medium">No professors found matching your search criteria.</p>
              <p className="mt-2 text-gray-500">Try adjusting your filters or search term.</p>
            </div>
          </motion.div>
        )}
    </motion.div>
  );
}
